import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, EmptyState, Modal } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, RefreshCw, Download, Upload, Scale } from "lucide-react";

const PAGE_SIZE = 20;

const AdminPanelMovesPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [undoFor, setUndoFor] = useState(null);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => { setPage(1); }, [search, typeFilter, stateFilter]);

  const load = async () => {
    try {
      const rows = await api.inventory.moves({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoaded(true);
    })();
  }, []);

  const submitUndo = async () => {
    if (!undoFor) return;
    setUndoing(true);
    try {
      await api.inventory.undo(undoFor.id);
      toast("Stock move undone and quantities reversed", "success");
      setUndoFor(null);
      await load();
    } catch (e) {
      toast(e.message || "Undo failed", "error");
    }
    setUndoing(false);
  };

  const stats = useMemo(() => {
    const incoming = data.filter((r) => r.move_type === "receipt" || r.move_type === "adjustment");
    const outgoing = data.filter((r) => r.move_type === "delivery");
    const qtyIn = incoming.reduce((s, r) => s + (Number(r.product_qty) || 0), 0);
    const qtyOut = outgoing.reduce((s, r) => s + (Number(r.product_qty) || 0), 0);
    return { count: data.length, incoming: incoming.length, outgoing: outgoing.length, qtyIn, qtyOut };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (typeFilter !== "all" && r.move_type !== typeFilter) return false;
      if (stateFilter !== "all" && r.state !== stateFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.product_name || ""} ${r.internal_ref || ""} ${r.reference || ""} ${r.src_location || ""} ${r.dest_location || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, typeFilter, stateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const typeLabel = (t) => {
    const map = { receipt: "Receipt", delivery: "Delivery", adjustment: "Adjustment", internal: "Internal", "in": "Incoming", "out": "Outgoing" };
    return map[t] || t || "—";
  };
  const typeColor = (t) => {
    if (t === "receipt") return { text: "#225f2b", bg: "#f0f7f0" };
    if (t === "delivery") return { text: "#be123c", bg: "#fff1f2" };
    if (t === "adjustment") return { text: "#b45309", bg: "#fffbeb" };
    return { text: "#475569", bg: "#f1f5f9" };
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="Stock Moves" subtitle="Audit trail of all stock movements" />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Total Moves" value={String(stats.count)} icon={RefreshCw} color="indigo" trend="All movements" />
        <StatCard label="Incoming" value={String(stats.incoming)} icon={Download} color="green" trend={`${fmt.qty(stats.qtyIn)} units in`} />
        <StatCard label="Outgoing" value={String(stats.outgoing)} icon={Upload} color="rose" trend={`${fmt.qty(stats.qtyOut)} units out`} />
        <StatCard label="Net Qty" value={fmt.qty(stats.qtyIn - stats.qtyOut)} icon={Scale} color="blue" trend="Units in − units out" />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product, ref, origin..." style={{ ...inputStyle, padding: "10px 12px 10px 36px" }} />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...inputStyle, minWidth: "150px", cursor: "pointer" }}>
          <option value="all">All Types</option>
          <option value="receipt">Receipt</option>
          <option value="delivery">Delivery</option>
          <option value="adjustment">Adjustment</option>
          <option value="internal">Internal</option>
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ ...inputStyle, minWidth: "140px", cursor: "pointer" }}>
          <option value="all">All States</option>
          <option value="done">Done</option>
          <option value="draft">Draft</option>
          <option value="cancel">Cancelled</option>
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="🔄" title="No stock moves" subtitle="Movements appear when stock is received, delivered or adjusted" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "120px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "170px" }}>Product</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>Code</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "80px" }}>Type</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "70px" }}>Qty</th>
                  <th className="ess-th" style={{ minWidth: "130px" }}>From</th>
                  <th className="ess-th" style={{ minWidth: "130px" }}>To</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>State</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "70px" }}></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row.id} className="ess-tr">
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.dateTime(row.date)}</td>
                    <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.product_name || row.product_id || "—"}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.internal_ref || "—"}</td>
                    <td className="ess-td" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <span style={{ background: typeColor(row.move_type).bg, color: typeColor(row.move_type).text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>{typeLabel(row.move_type)}</span>
                    </td>
                    <td className="ess-td" style={{ fontWeight: "700", color: row.move_type === "delivery" ? "#be123c" : "#225f2b", textAlign: "center", whiteSpace: "nowrap" }}>
                      {row.move_type === "delivery" ? "−" : "+"}{fmt.qty(row.product_qty)}
                    </td>
                    <td className="ess-td" style={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.src_location || "—"}</td>
                    <td className="ess-td" style={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.dest_location || "—"}</td>
                    <td className="ess-td" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <span style={{ background: row.state === "done" ? "#f0f7f0" : row.state === "cancel" ? "#fff1f2" : "#f1f5f9", color: row.state === "done" ? "#225f2b" : row.state === "cancel" ? "#be123c" : "#475569", padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>
                        {row.state === "done" ? "Done" : row.state === "cancel" ? "Cancelled" : row.state || "—"}
                      </span>
                    </td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {row.state === "done" && (
                        <button onClick={() => setUndoFor(row)} className="ess-btn ess-btn-danger">Undo</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={!!undoFor} onClose={() => setUndoFor(null)} title="Undo Stock Move">
        {undoFor && (
          <div>
            <p style={{ fontSize: "14px", color: "#475569", marginBottom: "20px" }}>
              Reverse the <strong>{typeLabel(undoFor.move_type)}</strong> of <strong>{undoFor.product_name || undoFor.product_id}</strong> — {fmt.qty(undoFor.product_qty)} units from {undoFor.src_location || "?"} to {undoFor.dest_location || "?"}. Quantities will be reversed.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
              <button onClick={() => setUndoFor(null)} className="ess-btn ess-btn-secondary">Cancel</button>
              <button onClick={submitUndo} disabled={undoing} className="ess-btn ess-btn-danger">
                {undoing ? "Undoing…" : "Confirm Undo"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPanelMovesPage;
